import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/vue-query";
import { computed, toValue, type MaybeRefOrGetter } from "vue";
import { useApi, useToaster } from "@shopexperts/shared";

/** Notes are an append-only timeline: the API exposes no update or delete route. */
export function useExpertNotes(expertId: MaybeRefOrGetter<number | string>) {
  const api = useApi();
  const queryClient = useQueryClient();
  const toaster = useToaster();

  const expertIdValue = computed(() => Number(toValue(expertId)));

  const {
    data,
    isLoading,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: computed(() => ["admin", "expert", expertIdValue.value, "notes"]),
    queryFn: async ({ pageParam }) => {
      return await api.admin.expertManager.listExpertNotes({
        params: { expertId: expertIdValue.value },
        query: { limit: 20, cursor: pageParam },
      });
    },
    getNextPageParam: (lastPage) => (lastPage.pagination.has_next ? lastPage.pagination.cursor : undefined),
    initialPageParam: undefined as string | undefined,
    enabled: computed(() => Number.isFinite(expertIdValue.value) && expertIdValue.value > 0),
    staleTime: 60 * 1000,
  });

  const notes = computed(() => data.value?.pages.flatMap((page) => page.data) ?? []);
  const totalNotes = computed(() => data.value?.pages[0]?.meta?.total ?? notes.value.length);

  const createNote = useMutation({
    mutationFn: async (body: string) => {
      return await api.admin.expertManager.createExpertNote({
        params: { expertId: expertIdValue.value },
        body: { body },
      });
    },
    onSuccess: () => {
      toaster.create({ title: "Note added", type: "success" });
      queryClient.invalidateQueries({ queryKey: ["admin", "expert", expertIdValue.value, "notes"] });
      // notes_count rides along on the list and detail payloads.
      queryClient.invalidateQueries({ queryKey: ["admin", "experts"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "expert"] });
    },
    onError: (error: Error) => {
      toaster.create({ title: `Failed to add note: ${error.message}`, type: "error" });
    },
  });

  return { notes, totalNotes, isLoading, error, refetch, hasNextPage, fetchNextPage, isFetchingNextPage, createNote };
}
